// Excel export: the Target Weights table (every selected book) and one book's Holdings
// History, styled like the Portfolio Viewer's export.
import XLSX from 'xlsx-js-style';

const NAVY = '012341';
const headerStyle = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
  fill: { fgColor: { rgb: NAVY } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
};
const acStyle = { font: { bold: true, sz: 10, color: { rgb: NAVY } }, fill: { fgColor: { rgb: 'F2F3F2' } } };
const acNum = { ...acStyle, alignment: { horizontal: 'center' }, numFmt: '0.0"%"' };
const cell = { font: { sz: 10 }, border: { bottom: { style: 'hair', color: { rgb: 'D9DDE3' } } } };
const numCell = { ...cell, alignment: { horizontal: 'center' }, numFmt: '0.0"%"' };
const totalStyle = { font: { bold: true, sz: 10, color: { rgb: NAVY } }, fill: { fgColor: { rgb: 'EDF5F4' } },
  border: { top: { style: 'medium', color: { rgb: NAVY } } } };
const totalNum = { ...totalStyle, alignment: { horizontal: 'center' }, numFmt: '0.0"%"' };
const metricNum = { ...cell, font: { bold: true, sz: 10 }, alignment: { horizontal: 'center' }, numFmt: '0.00"%"' };

function styleRows(ws, meta, width, firstNum) {
  for (let c = 0; c < width; c += 1) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = headerStyle;
  }
  meta.forEach((type, i) => {
    for (let c = 0; c < width; c += 1) {
      const ref = XLSX.utils.encode_cell({ r: i + 1, c });
      if (!ws[ref]) ws[ref] = { v: '', t: 's' };
      const isNum = c >= firstNum;
      ws[ref].s = type === 'ac' ? (isNum ? acNum : acStyle)
        : type === 'total' ? (isNum ? totalNum : totalStyle)
          : type === 'metric' ? (isNum ? metricNum : { ...cell, font: { bold: true, sz: 10 } })
            : (isNum ? numCell : cell);
    }
  });
}

export function exportWorkbook({ holdings, history, historyName, filename }) {
  const wb = XLSX.utils.book_new();

  if (holdings?.holdings?.length) {
    const cols = holdings.portfolios.filter((p) => p.as_at);
    const aoa = [['Fund Name', 'APIR', ...cols.map((p) => `${p.name} (${p.basis}, ${p.as_at})`)]];
    const meta = [];
    const groups = {};
    holdings.holdings.forEach((h) => { (groups[h.asset_class_group] = groups[h.asset_class_group] || []).push(h); });
    Object.entries(groups).forEach(([ac, rows]) => {
      aoa.push([ac, '', ...cols.map((p) => rows.reduce((s, h) => s + (h.weights[p.code] || 0), 0))]);
      meta.push('ac');
      rows.forEach((h) => { aoa.push([h.asset_name, h.apir_code || '', ...cols.map((p) => h.weights[p.code] ?? '')]); meta.push('data'); });
    });
    aoa.push(['Total', '', ...cols.map((p) => holdings.holdings.reduce((s, h) => s + (h.weights[p.code] || 0), 0))]);
    meta.push('total');
    [['FX Exposure', 'fx_exposure'], ['Growth Exposure', 'growth_exposure'], ['Defensive Exposure', 'defensive_exposure'],
      ['Fees & Costs (wtd)', 'fees_costs_wtd'], ['Net (wtd)', 'net_wtd']].forEach(([label, key]) => {
      aoa.push([label, '', ...cols.map((p) => holdings.summary?.[p.code]?.[key] ?? '')]);
      meta.push('metric');
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 44 }, { wch: 12 }, ...cols.map(() => ({ wch: 20 }))];
    styleRows(ws, meta, aoa[0].length, 2);
    XLSX.utils.book_append_sheet(wb, ws, 'Holdings');
  }

  if (history?.asset_classes?.length) {
    const dates = history.dates;
    const aoa = [['Holding', 'APIR', 'TR code', ...dates]];
    const meta = [];
    history.asset_classes.forEach((ac) => {
      aoa.push([ac.name, '', '', ...dates.map((d) => ac.totals[d] ?? '')]);
      meta.push('ac');
      ac.holdings.forEach((h) => {
        aoa.push([h.asset_name, h.apir_code || '', h.tr_code || '', ...dates.map((d) => h.values[d] ?? '')]);
        meta.push('data');
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 44 }, { wch: 12 }, { wch: 12 }, ...dates.map(() => ({ wch: 12 }))];
    styleRows(ws, meta, aoa[0].length, 3);
    XLSX.utils.book_append_sheet(wb, ws, `History ${historyName}`.slice(0, 31));
  }

  XLSX.writeFile(wb, filename);
}

const KIND = { new: 'Added', sold: 'Sold', up: 'Increased', down: 'Reduced' };

/** The Changes tab as one sheet: a row per fund change, with the book, effective date,
    who saved it, when, and the note on every row so the sheet filters cleanly. */
export function exportChanges(data, filename) {
  const header = ['Book', 'Effective date', 'Saved by', 'Saved at', 'Note', 'Fund', 'APIR', 'Asset class',
    'Change', 'Before %', 'After %', 'Change (pp)'];
  const aoa = [header];
  data.books.forEach((b) => b.changes.forEach((c) => {
    const ctx = [b.name, c.effective_date, c.source === 'seed' ? 'Refinitiv upload history' : (c.saved_by || ''),
      c.saved_at ? new Date(c.saved_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' }) : '', c.note || ''];
    if (!c.funds.length) aoa.push([...ctx, '(no fund weights changed)', '', '', '', '', '', '']);
    c.funds.forEach((f) => aoa.push([...ctx, f.name, f.apir_code || '', f.asset_class_group, KIND[f.kind],
      f.before ?? '', f.after ?? '', f.change]));
  }));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 26 }, { wch: 20 }, { wch: 60 }, { wch: 44 }, { wch: 12 },
    { wch: 22 }, { wch: 11 }, { wch: 9 }, { wch: 9 }, { wch: 11 }];
  for (let c = 0; c < header.length; c += 1) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    ws[ref].s = headerStyle;
  }
  for (let r = 1; r < aoa.length; r += 1) {
    for (let c = 0; c < header.length; c += 1) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { v: '', t: 's' };
      ws[ref].s = c >= 9 ? { ...numCell, numFmt: c === 11 ? '+0.00;-0.00;0.00' : '0.00' }
        : { ...cell, alignment: { wrapText: c === 4, vertical: 'top' } };
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Changes');
  XLSX.writeFile(wb, filename);
}
